import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import RichTextEditor, { MarkdownView, stripMarkdown } from '../components/RichTextEditor';
import { useAuth } from '../contexts/AuthContext';
import { jobsAPI, externalJobsAPI, uploadAPI, getShareUrl as getShareUrlBase } from '../lib/api';
import ShareMenu from '../components/ShareMenu';
import CompanyLogo from '../components/CompanyLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search, MapPin, DollarSign, Clock, Briefcase, Building2,
  ExternalLink, ChevronLeft, ChevronRight, Loader2, BookmarkPlus,
  Bookmark, Calendar, Plus, X, Filter, Share2, Copy, Check, Link2,
  Send, FileText, Upload, CheckCircle, MessageCircle, UserPlus, Edit2, Trash2, Sparkles
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Skeleton } from '../components/ui/skeleton';
import { useCommunity } from '../contexts/CommunityContext';
import LocationAutocomplete from '../components/LocationAutocomplete';
import usePageTitle from '../hooks/usePageTitle';

// Generate a Clearbit logo URL from a company name (no API call — just a URL)
const guessLogoUrl = (company) => {
  if (!company) return null;
  let domain = company.trim().toLowerCase();
  for (const s of [' inc', ' inc.', ' llc', ' ltd', ' corp', ' corporation', ' co', ' company', ' group', ' technologies', ' tech']) {
    if (domain.endsWith(s)) domain = domain.slice(0, -s.length).trim();
  }
  domain = domain.replace(/[\s,&]+/g, '').replace(/&/g, 'and');
  return `https://logo.clearbit.com/${domain}.com`;
};

const JOB_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
  { value: 'remote', label: 'Remote' },
];

const CATEGORIES = [
  'Accounting', 'Administrative', 'Aerospace', 'Agriculture', 'Architecture',
  'Arts & Entertainment', 'Aviation', 'Banking', 'Biotech', 'Business Development',
  'Construction', 'Consulting', 'Customer Service', 'Cybersecurity', 'Data Science',
  'Design', 'Education', 'Energy', 'Engineering', 'Environmental',
  'Fashion', 'Film & TV', 'Finance', 'Food & Beverage', 'Government',
  'Graphic Design', 'Healthcare', 'Hospitality', 'Human Resources', 'Information Technology',
  'Insurance', 'Journalism', 'Law Enforcement', 'Legal', 'Logistics',
  'Manufacturing', 'Marketing', 'Media', 'Medicine', 'Military',
  'Non-Profit', 'Operations', 'Pharmaceuticals', 'Photography', 'Product Management',
  'Project Management', 'Psychology', 'Public Relations', 'Publishing', 'Real Estate',
  'Research', 'Retail', 'Sales', 'Science', 'Social Work',
  'Software Engineering', 'Sports', 'Supply Chain', 'Teaching', 'Telecommunications',
  'Transportation', 'Travel & Tourism', 'UX/UI Design', 'Veterinary', 'Web Development',
  'Writing',
];

const WORK_MODES = [
  { value: 'remote', label: 'Remote' },
  { value: 'on_site', label: 'On-Site' },
  { value: 'hybrid', label: 'Hybrid' },
];

const SALARY_RANGES = [
  { value: '0-50', label: '$0–50K', min: 0, max: 50000 },
  { value: '50-100', label: '$50–100K', min: 50000, max: 100000 },
  { value: '100-150', label: '$100–150K', min: 100000, max: 150000 },
  { value: '150+', label: '$150K+', min: 150000, max: null },
];

const EXPERIENCE_LEVELS = [
  { value: 'entry', label: 'Entry' },
  { value: 'mid', label: 'Mid' },
  { value: 'senior', label: 'Senior' },
  { value: 'executive', label: 'Executive' },
];

const Jobs = () => {
  usePageTitle('Jobs');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { institutionFilter, communityView, communityLabel } = useCommunity();
  const [searchParams, setSearchParams] = useSearchParams();
  const [copiedLink, setCopiedLink] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [jobType, setJobType] = useState('all');
  const [location, setLocation] = useState('');
  const [confirmedLocation, setConfirmedLocation] = useState('');
  const [page, setPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState(null);
  const [savedJobs, setSavedJobs] = useState(new Set());
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [customCategory, setCustomCategory] = useState('');
  const [savedJobsList, setSavedJobsList] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);

  // My Posted Jobs / Applications Dashboard
  const [showPostedJobs, setShowPostedJobs] = useState(false);
  const [postedJobs, setPostedJobs] = useState([]);
  const [loadingPosted, setLoadingPosted] = useState(false);
  const [selectedPostedJob, setSelectedPostedJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const LIMIT = 24;

  // Filter chips state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedWorkMode, setSelectedWorkMode] = useState(null);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [selectedExperience, setSelectedExperience] = useState(null);

  // New job form
  const [newJob, setNewJob] = useState({
    title: '', company: '', location: '', description: '',
    job_type: 'full_time', salary_min: '', salary_max: '',
    requirements: '', work_mode: '', experience_level: '', category: '',
    external_apply_url: '',
  });

  // Smart paste: AI-parse a raw job posting into form fields
  const [pasteText, setPasteText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [applicationsTab, setApplicationsTab] = useState('applications'); // 'applications' | 'referrals'

  // Edit job posting
  const [editJobDialog, setEditJobDialog] = useState(null); // the job being edited, or null
  const [editJob, setEditJob] = useState({});
  const [savingEditJob, setSavingEditJob] = useState(false);
  const [deletingJob, setDeletingJob] = useState(false);
  const [jobLogoFile, setJobLogoFile] = useState(null);
  const [jobLogoPreview, setJobLogoPreview] = useState(null);
  const [fetchingLogo, setFetchingLogo] = useState(false);
  const [autoLogoUrl, setAutoLogoUrl] = useState(null);

  // External jobs
  const [jobTab, setJobTab] = useState('all');
  const [externalJobs, setExternalJobs] = useState([]);
  const [loadingExternal, setLoadingExternal] = useState(false);

  // Application state
  const [showApply, setShowApply] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [coverLetter, setCoverLetter] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [myApplications, setMyApplications] = useState([]);
  const [showMyApps, setShowMyApps] = useState(false);

  // Reset source filter when leaving All GradLink — the tab is hidden in that state,
  // so a stale 'alumni' or 'external' selection would silently exclude jobs.
  useEffect(() => {
    if (communityView !== 'gradlink' && jobTab !== 'all') {
      setJobTab('all');
    }
  }, [communityView, jobTab]);

  useEffect(() => {
    fetchJobs();
    fetchExternalJobs();
  }, [page, confirmedLocation, institutionFilter.institution, selectedCategory, selectedWorkMode, selectedSalary, selectedExperience]);

  // Load saved jobs on mount
  useEffect(() => {
    jobsAPI.savedJobs().then(res => {
      const list = Array.isArray(res.data) ? res.data : [];
      setSavedJobs(new Set(list.map(j => j.job_id)));
    }).catch(() => {});
    // Load dynamic categories (custom ones users added)
    jobsAPI.getCategories().then(res => {
      setDynamicCategories(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  // Merged list of default + dynamic categories (deduped, sorted)
  const allCategories = (() => {
    const defaultSet = new Set(CATEGORIES.map(c => c.toLowerCase()));
    const extras = dynamicCategories.filter(c => c && !defaultSet.has(c.toLowerCase()));
    return [...CATEGORIES, ...extras].sort();
  })();

  // Deep link: auto-open job from ?view= param (shared links)
  useEffect(() => {
    const viewJobId = searchParams.get('view');
    if (viewJobId && !selectedJob) {
      jobsAPI.get(viewJobId).then(res => {
        setSelectedJob(res.data);
        setSearchParams({}, { replace: true });
      }).catch(() => {
        // Try external jobs
        externalJobsAPI.list({ limit: 1 }).then(res => {
          const found = (res.data || []).find(j => j.job_id === viewJobId);
          if (found) setSelectedJob({ ...found, _external: true });
        }).catch(() => {});
        setSearchParams({}, { replace: true });
      });
    }
  }, [searchParams]);

  // Check application status when a job is selected
  useEffect(() => {
    if (selectedJob && !selectedJob._external) {
      checkApplicationStatus(selectedJob.job_id);
    } else {
      setApplicationStatus(null);
    }
  }, [selectedJob?.job_id]);

  const getShareUrl = (job) => getShareUrlBase('job', job.job_id);

  const handleCopyLink = async (job) => {
    try {
      await navigator.clipboard.writeText(getShareUrl(job));
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      toast.success('Link copied!');
    } catch { toast.error('Failed to copy link'); }
  };

  const handleShare = (platform, job) => {
    const url = encodeURIComponent(getShareUrl(job));
    const text = encodeURIComponent(`${job.title} at ${job.company} - Check out this opportunity on GradLink!`);
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const fetchExternalJobs = async () => {
    setLoadingExternal(true);
    try {
      const params = { limit: 50 };
      if (search) params.search = search;
      if (selectedWorkMode) params.work_mode = selectedWorkMode;
      if (selectedCategory) params.category = selectedCategory;
      if (selectedExperience) params.experience_level = selectedExperience;
      if (jobType && jobType !== 'all') params.job_type = jobType;
      if (selectedSalary) {
        const range = SALARY_RANGES.find(r => r.value === selectedSalary);
        if (range) {
          params.salary_min = range.min;
          if (range.max) params.salary_max = range.max;
        }
      }
      const res = await externalJobsAPI.list(params);
      setExternalJobs(Array.isArray(res.data) ? res.data : []);
    } catch { setExternalJobs([]); } finally { setLoadingExternal(false); }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = { limit: 100, ...institutionFilter };
      if (search) params.search = search;
      if (jobType && jobType !== 'all') params.job_type = jobType;
      if (confirmedLocation) params.location = confirmedLocation;
      if (selectedCategory) params.category = selectedCategory;
      if (selectedWorkMode) params.work_mode = selectedWorkMode;
      if (selectedSalary) {
        const range = SALARY_RANGES.find(r => r.value === selectedSalary);
        if (range) {
          params.salary_min = range.min;
          if (range.max) params.salary_max = range.max;
        }
      }
      if (selectedExperience) params.experience_level = selectedExperience;
      const res = await jobsAPI.list(params);
      const data = Array.isArray(res.data) ? res.data : [];
      setJobs(data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault?.();
    setConfirmedLocation(location);
    setPage(1);
    fetchJobs();
  };

  const toggleSave = async (jobId) => {
    const wasSaved = savedJobs.has(jobId);
    // Optimistic update
    setSavedJobs(prev => {
      const next = new Set(prev);
      if (wasSaved) next.delete(jobId); else next.add(jobId);
      return next;
    });
    try {
      if (wasSaved) {
        await jobsAPI.unsaveJob(jobId);
        toast.success('Job unsaved');
      } else {
        await jobsAPI.saveJob(jobId);
        toast.success('Job saved!');
      }
    } catch (error) {
      // Revert on error
      setSavedJobs(prev => {
        const next = new Set(prev);
        if (wasSaved) next.add(jobId); else next.delete(jobId);
        return next;
      });
      toast.error('Failed to update saved jobs');
    }
  };

  const openSavedJobs = async () => {
    setShowSaved(true);
    setLoadingSaved(true);
    try {
      const res = await jobsAPI.savedJobs();
      setSavedJobsList(Array.isArray(res.data) ? res.data : []);
    } catch { setSavedJobsList([]); }
    finally { setLoadingSaved(false); }
  };

  const openPostedJobs = async () => {
    setShowPostedJobs(true);
    setLoadingPosted(true);
    try {
      const res = await jobsAPI.myPostedJobs();
      setPostedJobs(Array.isArray(res.data) ? res.data : []);
    } catch { setPostedJobs([]); }
    finally { setLoadingPosted(false); }
  };

  const openJobApplications = async (job) => {
    setSelectedPostedJob(job);
    setApplicationsTab(job.external_apply_url ? 'referrals' : 'applications');
    setLoadingApps(true);
    try {
      const res = await jobsAPI.getJobApplications(job.job_id);
      setApplications(Array.isArray(res.data?.applications) ? res.data.applications : []);
    } catch { setApplications([]); }
    finally { setLoadingApps(false); }
    // Also fetch referrals if this job has external_apply_url
    if (job.external_apply_url) {
      setLoadingReferrals(true);
      try {
        const res = await jobsAPI.getReferrals(job.job_id);
        setReferrals(Array.isArray(res.data?.referrals) ? res.data.referrals : []);
      } catch { setReferrals([]); }
      finally { setLoadingReferrals(false); }
    } else {
      setReferrals([]);
    }
  };

  const updateApplicationStage = async (appId, stage) => {
    if (!selectedPostedJob) return;
    try {
      await jobsAPI.updateApplication(selectedPostedJob.job_id, appId, { stage });
      setApplications(prev => prev.map(a => a.application_id === appId ? { ...a, stage } : a));
      toast.success('Stage updated');
    } catch { toast.error('Failed to update'); }
  };

  const openEditJob = (job) => {
    setEditJobDialog(job);
    setEditJob({
      title: job.title || '',
      company: job.company || '',
      location: job.location || '',
      description: job.description || '',
      job_type: job.job_type || 'full_time',
      salary_min: job.salary_min || '',
      salary_max: job.salary_max || '',
      requirements: Array.isArray(job.requirements) ? job.requirements.join('\n') : (job.requirements || ''),
      work_mode: job.work_mode || '',
      experience_level: job.experience_level || '',
      category: job.category || '',
      external_apply_url: job.external_apply_url || '',
      is_active: job.is_active !== false,
    });
  };

  const saveEditJob = async () => {
    if (!editJobDialog) return;
    if (!editJob.title || !editJob.company || !editJob.description) {
      toast.error('Title, company, and description are required');
      return;
    }
    setSavingEditJob(true);
    try {
      const payload = {
        title: editJob.title,
        company: editJob.company,
        location: editJob.location,
        description: editJob.description,
        job_type: editJob.job_type,
        requirements: editJob.requirements ? editJob.requirements.split('\n').filter(r => r.trim()) : [],
        is_active: editJob.is_active,
      };
      if (editJob.salary_min) payload.salary_min = parseInt(editJob.salary_min);
      if (editJob.salary_max) payload.salary_max = parseInt(editJob.salary_max);
      if (editJob.work_mode) payload.work_mode = editJob.work_mode;
      if (editJob.experience_level) payload.experience_level = editJob.experience_level;
      if (editJob.category) payload.category = editJob.category;
      payload.external_apply_url = editJob.external_apply_url?.trim() || null;
      await jobsAPI.update(editJobDialog.job_id, payload);
      toast.success('Job updated!');
      setEditJobDialog(null);
      // Refresh both the main jobs list and the posted jobs dashboard
      fetchJobs();
      if (showPostedJobs) {
        const res = await jobsAPI.myPostedJobs();
        setPostedJobs(Array.isArray(res.data) ? res.data : []);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update job'); }
    finally { setSavingEditJob(false); }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}" at ${job.company}? This also removes all associated applications. This cannot be undone.`)) return;
    setDeletingJob(true);
    try {
      await jobsAPI.delete(job.job_id);
      toast.success('Job deleted');
      setEditJobDialog(null);
      fetchJobs();
      if (showPostedJobs) {
        const res = await jobsAPI.myPostedJobs();
        setPostedJobs(Array.isArray(res.data) ? res.data : []);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
    finally { setDeletingJob(false); }
  };

  // Check application status when viewing a job
  const checkApplicationStatus = async (jobId) => {
    try {
      const res = await jobsAPI.applicationStatus(jobId);
      setApplicationStatus(res.data);
    } catch { setApplicationStatus(null); }
  };

  const handleApply = async () => {
    if (!selectedJob) return;
    setApplying(true);
    try {
      let resume_url = '';
      if (resumeFile) {
        const uploadRes = await uploadAPI.marketplaceImage(resumeFile);
        resume_url = uploadRes.data.url;
      }
      await jobsAPI.apply(selectedJob.job_id, { cover_letter: coverLetter, resume_url });
      toast.success('Application submitted!');
      setShowApply(false);
      setCoverLetter('');
      setResumeFile(null);
      setApplicationStatus({ applied: true, stage: 'new' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally { setApplying(false); }
  };

  const fetchMyApplications = async () => {
    try {
      const res = await jobsAPI.myApplications();
      setMyApplications(res.data?.applications || []);
    } catch { setMyApplications([]); }
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return null;
    const fmt = (n) => n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${n}`;
    if (min && max) return `${fmt(min)} - ${fmt(max)}`;
    if (min) return `${fmt(min)}+`;
    return `Up to ${fmt(max)}`;
  };

  const fetchCompanyLogo = async (companyName) => {
    if (!companyName?.trim()) return;
    setFetchingLogo(true);
    try {
      const res = await jobsAPI.companyLogo(companyName.trim());
      if (res.data?.company_logo) {
        setAutoLogoUrl(res.data.company_logo);
      } else {
        setAutoLogoUrl(null);
      }
    } catch {
      setAutoLogoUrl(null);
    } finally {
      setFetchingLogo(false);
    }
  };

  const handleLogoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo too large. Max 5MB');
      return;
    }
    setJobLogoFile(file);
    setJobLogoPreview(URL.createObjectURL(file));
    setAutoLogoUrl(null); // Manual upload overrides auto-fetch
  };

  const removeJobLogo = () => {
    if (jobLogoPreview) URL.revokeObjectURL(jobLogoPreview);
    setJobLogoFile(null);
    setJobLogoPreview(null);
  };

  // Parse a pasted job posting and pre-fill the form
  const handleParseJob = async () => {
    const text = pasteText.trim();
    if (!text) {
      toast.error('Paste a job posting first');
      return;
    }
    setParsing(true);
    try {
      const res = await jobsAPI.parse(text);
      const p = res.data || {};
      // Map AI-extracted category to form-friendly value: known list → use it, else 'custom'
      const knownCategorySet = new Set(allCategories.map(c => c.toLowerCase()));
      let categoryValue = '';
      let customCat = '';
      if (p.category) {
        const match = allCategories.find(c => c.toLowerCase() === p.category.toLowerCase());
        if (match) {
          categoryValue = match;
        } else {
          categoryValue = 'custom';
          customCat = p.category;
        }
      }
      // Requirements arrive as array — Jobs form stores as newline-joined string
      const requirementsStr = Array.isArray(p.requirements) ? p.requirements.join('\n') : '';
      // Only set job_type if AI returned a recognized value; otherwise keep default
      const validJobTypes = new Set(JOB_TYPES.filter(t => t.value !== 'all').map(t => t.value));
      const jobType = validJobTypes.has(p.job_type) ? p.job_type : newJob.job_type;
      const validWorkModes = new Set(WORK_MODES.map(w => w.value));
      const workMode = validWorkModes.has(p.work_mode) ? p.work_mode : '';
      const validExpLevels = new Set(EXPERIENCE_LEVELS.map(e => e.value));
      const expLevel = validExpLevels.has(p.experience_level) ? p.experience_level : '';

      setNewJob({
        title: p.title || '',
        company: p.company || '',
        location: p.location || '',
        description: p.description || '',
        job_type: jobType,
        salary_min: p.salary_min != null ? String(p.salary_min) : '',
        salary_max: p.salary_max != null ? String(p.salary_max) : '',
        requirements: requirementsStr,
        work_mode: workMode,
        experience_level: expLevel,
        category: categoryValue,
        external_apply_url: p.external_apply_url || '',
      });
      setCustomCategory(customCat);
      // Auto-fetch company logo for the parsed company name
      if (p.company && !jobLogoFile) {
        fetchCompanyLogo(p.company);
      }
      // Tally filled fields for feedback
      const filled = ['title','company','location','description','job_type','work_mode','experience_level','category','external_apply_url']
        .filter(k => {
          if (k === 'job_type') return jobType && jobType !== newJob.job_type;
          if (k === 'work_mode') return !!workMode;
          if (k === 'experience_level') return !!expLevel;
          if (k === 'category') return !!categoryValue;
          return !!p[k];
        }).length
        + (p.salary_min != null || p.salary_max != null ? 1 : 0)
        + (Array.isArray(p.requirements) && p.requirements.length > 0 ? 1 : 0);
      toast.success(`Filled ${filled} field${filled === 1 ? '' : 's'} — review before posting`);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to parse posting';
      toast.error(msg);
    } finally {
      setParsing(false);
    }
  };

  const handleCreateJob = async () => {
    if (!newJob.title || !newJob.company || !newJob.description) {
      toast.error('Title, company, and description are required');
      return;
    }
    setCreating(true);
    try {
      // Upload logo if user provided one
      let logoUrl = autoLogoUrl || null;
      if (jobLogoFile) {
        const uploadRes = await uploadAPI.jobLogo(jobLogoFile);
        logoUrl = uploadRes.data.url;
      }
      const payload = {
        title: newJob.title,
        company: newJob.company,
        location: newJob.location,
        description: newJob.description,
        job_type: newJob.job_type,
        requirements: newJob.requirements ? newJob.requirements.split('\n').filter(r => r.trim()) : [],
        company_logo: logoUrl,
      };
      if (newJob.salary_min) payload.salary_min = parseInt(newJob.salary_min);
      if (newJob.salary_max) payload.salary_max = parseInt(newJob.salary_max);
      if (newJob.work_mode) payload.work_mode = newJob.work_mode;
      if (newJob.experience_level) payload.experience_level = newJob.experience_level;
      if (newJob.category === 'custom' && customCategory.trim()) {
        payload.category = customCategory.trim();
      } else if (newJob.category && newJob.category !== 'custom') {
        payload.category = newJob.category;
      }
      if (newJob.external_apply_url?.trim()) payload.external_apply_url = newJob.external_apply_url.trim();
      const res = await jobsAPI.create(payload);
      setJobs(prev => [res.data, ...prev]);
      setShowCreate(false);
      setNewJob({ title: '', company: '', location: '', description: '', job_type: 'full_time', salary_min: '', salary_max: '', requirements: '', work_mode: '', experience_level: '', category: '', external_apply_url: '' });
      setCustomCategory('');
      setPasteText('');
      // Refresh dynamic categories so the new one appears
      jobsAPI.getCategories().then(res => setDynamicCategories(Array.isArray(res.data) ? res.data : [])).catch(() => {});
      setJobLogoFile(null);
      setJobLogoPreview(null);
      setAutoLogoUrl(null);
      toast.success('Job posted!');
    } catch (error) {
      toast.error('Failed to post job');
    } finally {
      setCreating(false);
    }
  };

  const activeFilterCount = [selectedCategory, selectedWorkMode, selectedSalary, selectedExperience, confirmedLocation].filter(Boolean).length;

  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedWorkMode(null);
    setSelectedSalary(null);
    setSelectedExperience(null);
    setLocation('');
    setConfirmedLocation('');
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-dark-slate">Jobs Board</h1>
            <p className="text-slate-gray mt-1">Find opportunities from your alumni network</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={openSavedJobs} className="rounded-full h-11 px-4">
              <Bookmark className="w-4 h-4 mr-2" />Saved ({savedJobs.size})
            </Button>
            <Button variant="outline" onClick={() => { fetchMyApplications(); setShowMyApps(true); }} className="rounded-full h-11 px-4">
              <FileText className="w-4 h-4 mr-2" />My Apps
            </Button>
            <Button variant="outline" onClick={openPostedJobs} className="rounded-full h-11 px-4">
              <Briefcase className="w-4 h-4 mr-2" />My Postings
            </Button>
            <Button onClick={() => setShowCreate(true)} className="bg-dark-slate text-white rounded-full h-11 px-5">
              <Plus className="w-4 h-4 mr-2" />Post a Job
            </Button>
          </div>
        </div>

        {/* Community Filter Banner — shows current scope & how to change it */}
        <div className={`rounded-2xl px-4 py-3 mb-4 border flex items-start gap-3 ${
          communityView === 'gradlink'
            ? 'bg-emerald-50 border-emerald-200'
            : 'bg-amber-50 border-amber-200'
        }`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            communityView === 'gradlink' ? 'bg-emerald-100' : 'bg-amber-100'
          }`}>
            <Filter className={`w-4 h-4 ${communityView === 'gradlink' ? 'text-emerald-600' : 'text-amber-600'}`} />
          </div>
          <div className="flex-1 text-sm">
            {communityView === 'gradlink' ? (
              <>
                <p className="font-semibold text-emerald-900">Showing jobs across all of GradLink</p>
                <p className="text-emerald-700 text-xs mt-0.5">You're seeing alumni-posted jobs from every HBCU plus curated external listings. Use the header toggle to narrow down to your school or SpelHouse.</p>
              </>
            ) : (
              <>
                <p className="font-semibold text-amber-900">Filtered to {communityLabel}</p>
                <p className="text-amber-700 text-xs mt-0.5">Alumni jobs posted by members at other schools are hidden. Switch to <span className="font-semibold">All GradLink</span> in the header to see everything.</p>
              </>
            )}
          </div>
        </div>

        {/* Job Source Filter — only shown on All GradLink, since a school filter already hides external/other-school jobs */}
        {communityView === 'gradlink' && (
          <div className="flex items-center bg-slate-100 rounded-full p-1 mb-4">
            {[
              { value: 'all', label: 'All Jobs' },
              { value: 'alumni', label: 'Alumni' },
              { value: 'external', label: 'External' },
            ].map(tab => (
              <button key={tab.value} onClick={() => { setJobTab(tab.value); setPage(1); }}
                className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors ${jobTab === tab.value ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'}`}>
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <form onSubmit={handleSearch} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-ambient mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
              <Input placeholder="Search jobs, companies..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 rounded-xl bg-slate-50 border-slate-200" />
            </div>
            <Select value={jobType} onValueChange={(v) => { setJobType(v); }}>
              <SelectTrigger className="h-11 w-[150px] rounded-xl bg-slate-50 border-slate-200">
                <SelectValue placeholder="Job Type" />
              </SelectTrigger>
              <SelectContent>
                {JOB_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="submit" className="h-11 bg-dark-slate text-white rounded-xl px-6">
              <Search className="w-4 h-4 mr-2" />Search
            </Button>
          </div>
          <div className="mt-3">
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray z-10 pointer-events-none" />
              <LocationAutocomplete
                value={location}
                onChange={setLocation}
                onSelect={(city) => { setLocation(city); setConfirmedLocation(city); setPage(1); }}
                placeholder="Filter by location..."
                className="pl-9 h-10 rounded-xl bg-slate-50 border-slate-200 text-sm"
              />
            </div>
          </div>
        </form>

        {/* Filter Groups */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-ambient mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-gray" />
              <span className="text-sm font-medium text-dark-slate">Filters</span>
            </div>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="text-xs text-red-500 hover:text-red-700 flex items-center">
                <X className="w-3 h-3 mr-0.5" />Clear all ({activeFilterCount})
              </button>
            )}
          </div>
          <div className="space-y-4">
            {/* Field group */}
            <div>
              <span className="text-xs text-slate-gray font-semibold uppercase tracking-wide">Field</span>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {allCategories.map(cat => (
                  <button key={cat} onClick={() => { setSelectedCategory(selectedCategory === cat ? null : cat); setPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                      selectedCategory === cat
                        ? 'bg-dark-slate text-white border-dark-slate'
                        : 'bg-slate-50 text-slate-gray border-slate-200 hover:border-slate-300'
                    }`}>
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            {/* Work Mode group */}
            <div>
              <span className="text-xs text-slate-gray font-semibold uppercase tracking-wide">Work Mode</span>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {WORK_MODES.map(wm => (
                  <button key={wm.value} onClick={() => { setSelectedWorkMode(selectedWorkMode === wm.value ? null : wm.value); setPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                      selectedWorkMode === wm.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-slate-50 text-slate-gray border-slate-200 hover:border-slate-300'
                    }`}>
                    {wm.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Salary group */}
            <div>
              <span className="text-xs text-slate-gray font-semibold uppercase tracking-wide">Salary</span>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {SALARY_RANGES.map(sr => (
                  <button key={sr.value} onClick={() => { setSelectedSalary(selectedSalary === sr.value ? null : sr.value); setPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                      selectedSalary === sr.value
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-slate-50 text-slate-gray border-slate-200 hover:border-slate-300'
                    }`}>
                    {sr.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Experience group */}
            <div>
              <span className="text-xs text-slate-gray font-semibold uppercase tracking-wide">Experience</span>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {EXPERIENCE_LEVELS.map(el => (
                  <button key={el.value} onClick={() => { setSelectedExperience(selectedExperience === el.value ? null : el.value); setPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                      selectedExperience === el.value
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-slate-50 text-slate-gray border-slate-200 hover:border-slate-300'
                    }`}>
                    {el.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Job Listings - Unified View */}
        {(loading && loadingExternal) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2 mb-3" />
                <Skeleton className="h-4 w-full mb-1" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        ) : (() => {
          // Merge and filter jobs based on active tab
          const alumniJobs = jobs.map(j => ({ ...j, _source: 'alumni' }));
          const extJobs = externalJobs.map(j => ({ ...j, _source: 'external' }));
          let allJobs = jobTab === 'all' ? [...alumniJobs, ...extJobs] :
                        jobTab === 'alumni' ? alumniJobs : extJobs;
          // Sort by created_at descending
          allJobs.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
          // Paginate
          const pageJobs = allJobs.slice((page - 1) * LIMIT, page * LIMIT);

          if (pageJobs.length === 0) return (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-ambient">
              <Briefcase className="w-12 h-12 text-slate-gray mx-auto mb-4" />
              <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">No jobs found</h3>
              <p className="text-slate-gray">Try adjusting your search or filters</p>
            </div>
          );

          return (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {pageJobs.map(job => {
                  const isExternal = job._source === 'external';
                  const CardWrapper = isExternal ? 'a' : 'div';
                  const cardProps = isExternal
                    ? { href: job.external_url, target: '_blank', rel: 'noopener noreferrer' }
                    : { onClick: () => setSelectedJob(job) };

                  return (
                    <CardWrapper key={job.job_id} {...cardProps}
                      className="bg-white rounded-2xl p-5 border border-slate-100 shadow-ambient hover:shadow-hover transition-shadow cursor-pointer block">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <CompanyLogo company={job.company} logo={job.company_logo} size="md" />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-bold text-lg text-dark-slate line-clamp-1">{job.title}</h3>
                            <p className="text-slate-gray mt-1">{job.company}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); const url = getShareUrl(job); navigator.clipboard.writeText(url); toast.success('Link copied!'); }}
                            className="p-2 rounded-full hover:bg-slate-100" title="Share">
                            <Share2 className="w-4 h-4 text-slate-gray" />
                          </button>
                          {!isExternal && (
                            <button onClick={(e) => { e.stopPropagation(); toggleSave(job.job_id); }}
                              className="p-2 rounded-full hover:bg-slate-100">
                              {savedJobs.has(job.job_id)
                                ? <Bookmark className="w-5 h-5 text-dark-slate fill-current" />
                                : <BookmarkPlus className="w-5 h-5 text-slate-gray" />}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {isExternal ? (
                          <Badge variant="secondary" className="bg-mint-fresh/10 text-mint-fresh text-xs">External</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-dark-slate/10 text-dark-slate text-xs">Alumni</Badge>
                        )}
                        {job.location && <Badge variant="secondary" className="bg-slate-100 text-slate-gray text-xs"><MapPin className="w-3 h-3 mr-1" />{job.location}</Badge>}
                        {job.job_type && <Badge variant="secondary" className="bg-blue-50 text-blue-700 text-xs">{job.job_type.replace('_', '-')}</Badge>}
                        {job.work_mode && <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 text-xs">{job.work_mode.replace('_', ' ')}</Badge>}
                        {!isExternal && formatSalary(job.salary_min, job.salary_max) && (
                          <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 text-xs">
                            <DollarSign className="w-3 h-3 mr-0.5" />{formatSalary(job.salary_min, job.salary_max)}
                          </Badge>
                        )}
                        {job.category && <Badge variant="secondary" className="bg-amber-50 text-amber-700 text-xs">{job.category}</Badge>}
                        {isExternal && job.source && <Badge variant="secondary" className="bg-slate-50 text-slate-gray text-xs">via {job.source}</Badge>}
                      </div>
                      {job.description && <p className="text-sm text-slate-gray line-clamp-2 mb-3">{stripMarkdown((job.description || '').replace(/<[^>]*>/g, ''))}</p>}

                      {/* Connect / Message for alumni jobs */}
                      {!isExternal && job.posted_by && job.posted_by !== user?.user_id && (
                        <div className="flex gap-2 mb-3">
                          <Button size="sm" variant="outline" className="rounded-full text-xs flex-1"
                            onClick={(e) => { e.stopPropagation(); navigate(`/chat/${job.posted_by}`); }}>
                            <MessageCircle className="w-3.5 h-3.5 mr-1" />Message {job.posted_by_name?.split(' ')[0] || 'Poster'}
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full text-xs"
                            onClick={(e) => { e.stopPropagation(); navigate(`/profile/${job.posted_by}`); }}>
                            <UserPlus className="w-3.5 h-3.5 mr-1" />View Profile
                          </Button>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-xs text-slate-gray">
                        <span className="flex items-center"><Clock className="w-3 h-3 mr-1" />
                          {job.created_at ? formatDistanceToNow(new Date(job.created_at), { addSuffix: true }) : 'Recently'}
                        </span>
                        {!isExternal && job.institution && <Badge variant="outline" className="text-[10px]">{job.institution}</Badge>}
                        {isExternal && <ExternalLink className="w-3 h-3" />}
                      </div>
                    </CardWrapper>
                  );
                })}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-center space-x-3">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-full">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-gray font-medium">Page {page}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={pageJobs.length < LIMIT} className="rounded-full">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </>
          );
        })()}

        {/* Job Detail Modal */}
        <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto">
            {selectedJob && (
              <>
                {/* Hero photo — renders flyer-style images at full size, small logos still fit nicely */}
                {selectedJob.company_logo && (
                  <div className="w-full bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl py-5 mb-2 flex items-center justify-center">
                    <img
                      src={selectedJob.company_logo}
                      alt={selectedJob.company || ''}
                      className="max-w-full max-h-80 object-contain rounded-lg"
                      onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                    />
                  </div>
                )}
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-dark-slate pr-8">{selectedJob.title}</DialogTitle>
                </DialogHeader>
                <div className="mt-4 space-y-4">
                  <div className="flex items-center gap-3">
                    {!selectedJob.company_logo && (
                      <CompanyLogo company={selectedJob.company} logo={selectedJob.company_logo} size="lg" className="border border-slate-100" />
                    )}
                    <span className="font-medium text-dark-slate text-lg">{selectedJob.company}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.location && <Badge variant="secondary" className="bg-slate-100"><MapPin className="w-3 h-3 mr-1" />{selectedJob.location}</Badge>}
                    {selectedJob.job_type && <Badge variant="secondary" className="bg-blue-50 text-blue-700">{selectedJob.job_type.replace('_', '-')}</Badge>}
                    {selectedJob.work_mode && <Badge variant="secondary" className="bg-indigo-50 text-indigo-700">{selectedJob.work_mode.replace('_', ' ')}</Badge>}
                    {formatSalary(selectedJob.salary_min, selectedJob.salary_max) && (
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-700"><DollarSign className="w-3 h-3 mr-0.5" />{formatSalary(selectedJob.salary_min, selectedJob.salary_max)}</Badge>
                    )}
                    {selectedJob.experience_level && <Badge variant="secondary" className="bg-purple-50 text-purple-700">{selectedJob.experience_level}</Badge>}
                    {selectedJob.category && <Badge variant="secondary" className="bg-amber-50 text-amber-700">{selectedJob.category}</Badge>}
                    {selectedJob.created_at && (
                      <Badge variant="secondary" className="bg-slate-50"><Calendar className="w-3 h-3 mr-1" />Posted {formatDistanceToNow(new Date(selectedJob.created_at), { addSuffix: true })}</Badge>
                    )}
                  </div>
                  {selectedJob.posted_by_name && (
                    <div className="bg-mint-fresh/20 rounded-xl p-3 text-sm">
                      Posted by <span className="font-semibold text-dark-slate">{selectedJob.posted_by_name}</span>
                      {selectedJob.institution && <span className="text-slate-gray"> from {selectedJob.institution}</span>}
                    </div>
                  )}
                  {selectedJob.description && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2">Description</h4>
                      <MarkdownView text={selectedJob.description} />
                    </div>
                  )}
                  {selectedJob.requirements?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2">Requirements</h4>
                      <ul className="space-y-1">
                        {selectedJob.requirements.map((req, i) => (
                          <li key={i} className="text-slate-gray text-sm flex items-start">
                            <span className="w-1.5 h-1.5 rounded-full bg-dark-slate mt-2 mr-2 flex-shrink-0" />
                            <span><MarkdownView text={req} className="inline" /></span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="flex gap-3 pt-4 border-t border-slate-100">
                    {applicationStatus?.applied ? (
                      <Button disabled className="flex-1 h-12 bg-emerald-50 text-emerald-700 rounded-full font-semibold border border-emerald-200">
                        <CheckCircle className="w-4 h-4 mr-2" />Applied — {applicationStatus.stage?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </Button>
                    ) : selectedJob?._external ? (
                      <a href={selectedJob.external_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button className="w-full h-12 bg-dark-slate text-white rounded-full font-semibold">
                          <ExternalLink className="w-4 h-4 mr-2" />Apply on Site
                        </Button>
                      </a>
                    ) : selectedJob?.external_apply_url ? (
                      <Button className="flex-1 h-12 bg-dark-slate text-white rounded-full font-semibold"
                        onClick={async () => {
                          try { await jobsAPI.logReferral(selectedJob.job_id); } catch {}
                          window.open(selectedJob.external_apply_url, '_blank', 'noopener,noreferrer');
                        }}>
                        <ExternalLink className="w-4 h-4 mr-2" />Apply at {selectedJob.company}
                      </Button>
                    ) : (
                      <Button className="flex-1 h-12 bg-dark-slate text-white rounded-full font-semibold"
                        onClick={() => { setCoverLetter(''); setResumeFile(null); setShowApply(true); }}>
                        <Send className="w-4 h-4 mr-2" />Apply Now
                      </Button>
                    )}
                    <Button variant="outline" className="h-12 rounded-full px-6" onClick={() => toggleSave(selectedJob.job_id)}>
                      {savedJobs.has(selectedJob.job_id) ? <><Bookmark className="w-4 h-4 mr-2 fill-current" />Saved</> : <><BookmarkPlus className="w-4 h-4 mr-2" />Save</>}
                    </Button>
                  </div>

                  {/* Connect with poster (alumni jobs only) */}
                  {selectedJob.posted_by && selectedJob.posted_by !== user?.user_id && selectedJob._source !== 'external' && (
                    <div className="bg-mint-fresh/10 rounded-xl p-4">
                      <p className="text-sm font-medium text-dark-slate mb-2">
                        Posted by {selectedJob.posted_by_name || 'an alum'}
                        {selectedJob.institution && <span className="text-slate-gray font-normal"> from {selectedJob.institution}</span>}
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" className="rounded-full bg-dark-slate text-white" onClick={() => { setSelectedJob(null); navigate(`/chat/${selectedJob.posted_by}`); }}>
                          <MessageCircle className="w-3.5 h-3.5 mr-1.5" />Message
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setSelectedJob(null); navigate(`/profile/${selectedJob.posted_by}`); }}>
                          <UserPlus className="w-3.5 h-3.5 mr-1.5" />View Profile
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Share */}
                  <div className="pt-3">
                    <p className="text-xs text-slate-gray mb-2 font-medium">Share this opportunity</p>
                    <ShareMenu url={getShareUrl(selectedJob)} title={`${selectedJob.title} at ${selectedJob.company}`} />
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Post a Job Modal */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-dark-slate">Post a Job</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Smart Paste — AI extracts fields from a raw job posting */}
              <div className="rounded-xl border border-dark-slate/15 bg-gradient-to-br from-indigo-50/40 to-purple-50/30 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-dark-slate" />
                  <Label className="text-sm font-semibold text-dark-slate">Paste a job posting (fills the form with AI)</Label>
                </div>
                <p className="text-[11px] text-slate-gray mb-2 leading-relaxed">
                  Got a posting from a recruiter, email, LinkedIn, or another job board? Paste it here and AI will extract the title, company, salary, description, requirements, and more. Review and adjust below before posting.
                </p>
                <Textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="Paste the full job posting text here..."
                  rows={4}
                  className="rounded-xl bg-white text-sm"
                  disabled={parsing}
                />
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    type="button"
                    onClick={handleParseJob}
                    disabled={parsing || !pasteText.trim()}
                    className="bg-dark-slate text-white rounded-full h-9 px-4 text-xs"
                  >
                    {parsing ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Parsing...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Parse with AI</>}
                  </Button>
                  {pasteText && !parsing && (
                    <Button type="button" variant="ghost" onClick={() => setPasteText('')} className="h-9 px-3 text-xs text-slate-gray">
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Job Title *</Label>
                  <Input placeholder="e.g. Software Engineer" value={newJob.title}
                    onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
                    className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>Company *</Label>
                  <Input placeholder="e.g. Google" value={newJob.company}
                    onChange={(e) => setNewJob({ ...newJob, company: e.target.value })}
                    onBlur={(e) => { if (e.target.value.trim() && !jobLogoFile) fetchCompanyLogo(e.target.value); }}
                    className="mt-1 rounded-xl" />
                </div>
              </div>

              {/* Company Logo */}
              <div>
                <Label>Company Logo or Job Flyer</Label>
                <div className="mt-1 flex items-start gap-4">
                  {/* Preview — taller box so portrait-orientation flyers preview properly */}
                  <div className="relative">
                    {(jobLogoPreview || autoLogoUrl) ? (
                      <div className="relative">
                        <img
                          src={jobLogoPreview || autoLogoUrl}
                          alt="Company logo or flyer"
                          className="w-28 h-36 rounded-xl object-contain bg-slate-50 p-2 border border-slate-200"
                          onError={(e) => { e.target.style.display = 'none'; setAutoLogoUrl(null); }}
                        />
                        <button
                          type="button"
                          onClick={() => { removeJobLogo(); setAutoLogoUrl(null); }}
                          className="absolute -top-1.5 -right-1.5 bg-dark-slate text-white rounded-full p-0.5 hover:bg-red-500 transition-colors shadow-sm"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        {autoLogoUrl && !jobLogoFile && (
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                            Auto-detected
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="w-28 h-36 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                        {fetchingLogo ? (
                          <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                        ) : (
                          <Building2 className="w-6 h-6 text-slate-300" />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <Button variant="outline" size="sm" className="rounded-full text-xs" asChild>
                        <span><Upload className="w-3.5 h-3.5 mr-1.5" />Upload Image</span>
                      </Button>
                      <input type="file" accept="image/*" className="hidden" onChange={handleLogoFileSelect} />
                    </label>
                    <p className="text-[11px] text-slate-gray mt-1.5 leading-snug">
                      {fetchingLogo ? 'Looking up company logo...' : 'Upload a square company logo or a tall portrait-style job flyer (IG-friendly). It displays large in the job detail.'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Location</Label>
                  <Input placeholder="e.g. Atlanta, GA" value={newJob.location}
                    onChange={(e) => setNewJob({ ...newJob, location: e.target.value })}
                    className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>Job Type</Label>
                  <Select value={newJob.job_type} onValueChange={(v) => setNewJob({ ...newJob, job_type: v })}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {JOB_TYPES.filter(t => t.value !== 'all').map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>Work Mode</Label>
                  <Select value={newJob.work_mode || 'none'} onValueChange={(v) => setNewJob({ ...newJob, work_mode: v === 'none' ? '' : v })}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {WORK_MODES.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Experience Level</Label>
                  <Select value={newJob.experience_level || 'none'} onValueChange={(v) => setNewJob({ ...newJob, experience_level: v === 'none' ? '' : v })}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {EXPERIENCE_LEVELS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={newJob.category === 'custom' ? 'custom' : (newJob.category || 'none')}
                    onValueChange={(v) => {
                      if (v === 'none') { setNewJob({ ...newJob, category: '' }); setCustomCategory(''); }
                      else if (v === 'custom') { setNewJob({ ...newJob, category: 'custom' }); }
                      else { setNewJob({ ...newJob, category: v }); setCustomCategory(''); }
                    }}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="none">Not specified</SelectItem>
                      {allCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      <SelectItem value="custom">+ Add new category...</SelectItem>
                    </SelectContent>
                  </Select>
                  {newJob.category === 'custom' && (
                    <Input
                      placeholder="Enter new category (e.g. Space Technology)"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      className="mt-2 rounded-xl"
                      autoFocus
                    />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Min Salary</Label>
                  <Input type="number" placeholder="e.g. 60000" value={newJob.salary_min}
                    onChange={(e) => setNewJob({ ...newJob, salary_min: e.target.value })}
                    className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>Max Salary</Label>
                  <Input type="number" placeholder="e.g. 120000" value={newJob.salary_max}
                    onChange={(e) => setNewJob({ ...newJob, salary_max: e.target.value })}
                    className="mt-1 rounded-xl" />
                </div>
              </div>
              <div>
                <Label>Description *</Label>
                <div className="mt-1">
                  <RichTextEditor
                    value={newJob.description}
                    onChange={(v) => setNewJob({ ...newJob, description: v })}
                    placeholder="Describe the role, responsibilities, and ideal candidate... Use **bold**, *italic*, or the toolbar."
                    minHeight={140}
                  />
                </div>
              </div>
              <div>
                <Label>Requirements (one per line)</Label>
                <div className="mt-1">
                  <RichTextEditor
                    value={newJob.requirements}
                    onChange={(v) => setNewJob({ ...newJob, requirements: v })}
                    placeholder="3+ years experience in React&#10;Bachelor's degree in CS&#10;Strong communication skills"
                    minHeight={100}
                  />
                </div>
              </div>

              {/* External Apply URL — optional */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <Label className="text-sm font-semibold">
                  External Application URL <span className="text-slate-gray font-normal">(optional)</span>
                </Label>
                <Input
                  type="url"
                  placeholder="https://company.com/careers/job-id"
                  value={newJob.external_apply_url}
                  onChange={(e) => setNewJob({ ...newJob, external_apply_url: e.target.value })}
                  className="mt-2 rounded-xl"
                />
                <p className="text-[11px] text-slate-gray mt-2 leading-relaxed">
                  If set, the "Apply Now" button will open this URL in a new tab instead of using GradLink's built-in application flow. You'll still see everyone who clicked through in your "My Postings" dashboard under a "Referrals" tab.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={handleCreateJob} disabled={creating}
                  className="flex-1 h-12 bg-dark-slate text-white rounded-full font-semibold">
                  {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Posting...</> : <><Plus className="w-4 h-4 mr-2" />Post Job</>}
                </Button>
                <Button variant="outline" onClick={() => setShowCreate(false)} className="h-12 rounded-full px-6">Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Apply Dialog */}
        <Dialog open={showApply} onOpenChange={setShowApply}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Apply to {selectedJob?.title}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-gray">{selectedJob?.company}{selectedJob?.location ? ` — ${selectedJob.location}` : ''}</p>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Cover Letter <span className="text-slate-gray text-xs">(optional)</span></Label>
                <Textarea value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)}
                  placeholder="Why are you a great fit for this role?" rows={5} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Resume <span className="text-slate-gray text-xs">(optional)</span></Label>
                <div className="mt-1">
                  {resumeFile ? (
                    <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-4 py-3">
                      <FileText className="w-4 h-4 text-slate-gray" />
                      <span className="text-sm text-dark-slate flex-1 truncate">{resumeFile.name}</span>
                      <button onClick={() => setResumeFile(null)} className="text-slate-gray hover:text-dark-slate"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 bg-slate-50 rounded-xl px-4 py-3 cursor-pointer hover:bg-slate-100 transition-colors">
                      <Upload className="w-4 h-4 text-slate-gray" />
                      <span className="text-sm text-slate-gray">Upload resume (PDF, DOC)</span>
                      <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
                        setResumeFile(file || null);
                      }} />
                    </label>
                  )}
                </div>
              </div>
              <Button onClick={handleApply} disabled={applying} className="w-full h-12 bg-dark-slate text-white rounded-full font-semibold">
                {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : <><Send className="w-4 h-4 mr-2" />Submit Application</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* My Applications Dialog */}
        <Dialog open={showMyApps} onOpenChange={setShowMyApps}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">My Applications</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-4">
              {myApplications.length === 0 ? (
                <p className="text-slate-gray text-sm text-center py-8">No applications yet. Find a job and apply!</p>
              ) : myApplications.map(app => (
                <div key={app.application_id} className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-dark-slate text-sm">{app.job_title || 'Position'}</h4>
                      <p className="text-xs text-slate-gray">{app.company}{app.job_location ? ` — ${app.job_location}` : ''}</p>
                    </div>
                    <Badge className={`text-[10px] ${
                      app.stage === 'offered' || app.stage === 'hired' ? 'bg-emerald-100 text-emerald-700' :
                      app.stage === 'rejected' ? 'bg-red-100 text-red-700' :
                      app.stage === 'interviewed' ? 'bg-blue-100 text-blue-700' :
                      app.stage === 'contacted' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-200 text-slate-600'
                    }`}>{app.stage?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                  </div>
                  {app.created_at && <p className="text-[10px] text-slate-gray mt-1">Applied {formatDistanceToNow(new Date(app.created_at), { addSuffix: true })}</p>}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* ════════ Saved Jobs Dialog ════════ */}
        <Dialog open={showSaved} onOpenChange={setShowSaved}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-dark-slate flex items-center gap-2">
                <Bookmark className="w-5 h-5 fill-current" />Saved Jobs
              </DialogTitle>
            </DialogHeader>
            {loadingSaved ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-gray" /></div>
            ) : savedJobsList.length === 0 ? (
              <div className="text-center py-12">
                <Bookmark className="w-12 h-12 text-slate-gray mx-auto mb-4" />
                <h3 className="font-bold text-lg text-dark-slate mb-2">No saved jobs</h3>
                <p className="text-slate-gray text-sm">Click the bookmark icon on any job to save it for later</p>
              </div>
            ) : (
              <div className="space-y-3 mt-4">
                {savedJobsList.map(job => (
                  <div key={job.job_id} className="bg-white rounded-2xl p-4 border border-slate-100 hover:shadow-ambient transition-shadow cursor-pointer"
                    onClick={() => { setShowSaved(false); if (job._source === 'external' && job.external_url) { window.open(job.external_url, '_blank'); } else { setSelectedJob(job); } }}>
                    <div className="flex items-start gap-3">
                      <CompanyLogo company={job.company} logo={job.company_logo} size="md" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-dark-slate line-clamp-1">{job.title}</h3>
                        <p className="text-sm text-slate-gray">{job.company}</p>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {job.location && <Badge variant="secondary" className="text-[10px] bg-slate-100"><MapPin className="w-2.5 h-2.5 mr-1" />{job.location}</Badge>}
                          {job.job_type && <Badge variant="secondary" className="text-[10px] bg-blue-50 text-blue-700">{job.job_type.replace('_', '-')}</Badge>}
                          {job._source === 'external' && <Badge variant="secondary" className="text-[10px] bg-mint-fresh/10 text-mint-fresh">External</Badge>}
                        </div>
                        <p className="text-[10px] text-slate-gray mt-1.5">Saved {job.saved_at ? formatDistanceToNow(new Date(job.saved_at), { addSuffix: true }) : ''}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); toggleSave(job.job_id); setSavedJobsList(prev => prev.filter(j => j.job_id !== job.job_id)); }}
                        className="p-2 rounded-full hover:bg-slate-100" title="Remove from saved">
                        <X className="w-4 h-4 text-slate-gray" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ════════ My Posted Jobs Dialog ════════ */}
        <Dialog open={showPostedJobs} onOpenChange={(open) => { if (!open) { setShowPostedJobs(false); setSelectedPostedJob(null); setSelectedApplication(null); } }}>
          <DialogContent className="max-w-3xl rounded-2xl max-h-[85vh] overflow-y-auto">
            {selectedApplication ? (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl text-dark-slate flex items-center gap-2">
                    <button onClick={() => setSelectedApplication(null)} className="p-1 rounded-full hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
                    Application Details
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {/* Candidate */}
                  <div className="bg-slate-50 rounded-xl p-4 flex items-start gap-3">
                    {selectedApplication.candidate?.profile_photo ? (
                      <img src={selectedApplication.candidate.profile_photo} alt="" className="w-14 h-14 rounded-full object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-mint-fresh flex items-center justify-center text-dark-slate text-xl font-semibold">
                        {selectedApplication.candidate?.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-bold text-dark-slate">{selectedApplication.candidate?.name || 'Applicant'}</h3>
                      {selectedApplication.candidate?.title && <p className="text-sm text-slate-gray">{selectedApplication.candidate.title}{selectedApplication.candidate.company ? ` at ${selectedApplication.candidate.company}` : ''}</p>}
                      {selectedApplication.candidate?.institution && <p className="text-xs text-slate-gray mt-0.5">{selectedApplication.candidate.institution}{selectedApplication.candidate.graduation_year ? ` '${String(selectedApplication.candidate.graduation_year).slice(-2)}` : ''}</p>}
                      {selectedApplication.candidate?.current_location && <p className="text-xs text-slate-gray flex items-center mt-0.5"><MapPin className="w-3 h-3 mr-1" />{selectedApplication.candidate.current_location}</p>}
                    </div>
                  </div>

                  {/* Contact buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {selectedApplication.candidate?.email && (
                      <a href={`mailto:${selectedApplication.candidate.email}`}>
                        <Button size="sm" variant="outline" className="rounded-full text-xs"><FileText className="w-3.5 h-3.5 mr-1" />Email</Button>
                      </a>
                    )}
                    <Button size="sm" variant="outline" className="rounded-full text-xs"
                      onClick={() => { setShowPostedJobs(false); navigate(`/chat/${selectedApplication.candidate_id}`); }}>
                      <MessageCircle className="w-3.5 h-3.5 mr-1" />Message
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-xs"
                      onClick={() => { setShowPostedJobs(false); navigate(`/profile/${selectedApplication.candidate_id}`); }}>
                      <UserPlus className="w-3.5 h-3.5 mr-1" />View Profile
                    </Button>
                    {selectedApplication.resume_url && (
                      <a href={selectedApplication.resume_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" className="rounded-full text-xs bg-dark-slate text-white"><FileText className="w-3.5 h-3.5 mr-1" />View Resume</Button>
                      </a>
                    )}
                  </div>

                  {/* Stage */}
                  <div>
                    <label className="text-sm font-medium text-dark-slate">Stage</label>
                    <Select value={selectedApplication.stage || 'new'} onValueChange={(v) => { updateApplicationStage(selectedApplication.application_id, v); setSelectedApplication(p => ({ ...p, stage: v })); }}>
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">🔵 New</SelectItem>
                        <SelectItem value="reviewed">📋 Reviewed</SelectItem>
                        <SelectItem value="contacted">💬 Contacted</SelectItem>
                        <SelectItem value="interviewed">🤝 Interviewed</SelectItem>
                        <SelectItem value="offered">💼 Offered</SelectItem>
                        <SelectItem value="hired">✅ Hired</SelectItem>
                        <SelectItem value="rejected">❌ Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Cover letter */}
                  {selectedApplication.cover_letter && (
                    <div>
                      <label className="text-sm font-medium text-dark-slate">Cover Letter</label>
                      <div className="bg-slate-50 rounded-xl p-4 mt-1 text-sm text-slate-gray whitespace-pre-wrap">{selectedApplication.cover_letter}</div>
                    </div>
                  )}

                  <p className="text-xs text-slate-gray">Applied {selectedApplication.created_at ? formatDistanceToNow(new Date(selectedApplication.created_at), { addSuffix: true }) : ''}</p>
                </div>
              </>
            ) : selectedPostedJob ? (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl text-dark-slate flex items-center gap-2">
                    <button onClick={() => { setSelectedPostedJob(null); setApplications([]); setReferrals([]); }} className="p-1 rounded-full hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
                    {selectedPostedJob.title}
                  </DialogTitle>
                </DialogHeader>

                {/* Tab switcher — shown only if job has external_apply_url */}
                {selectedPostedJob.external_apply_url && (
                  <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1 mb-4 mt-2">
                    <button onClick={() => setApplicationsTab('applications')}
                      className={`flex-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${applicationsTab === 'applications' ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'}`}>
                      Native ({applications.length})
                    </button>
                    <button onClick={() => setApplicationsTab('referrals')}
                      className={`flex-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${applicationsTab === 'referrals' ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'}`}>
                      Referrals ({referrals.length})
                    </button>
                  </div>
                )}

                {/* Native Applications tab */}
                {applicationsTab === 'applications' && (
                  loadingApps ? (
                    <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-gray" /></div>
                  ) : applications.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-slate-gray mx-auto mb-4" />
                      <h3 className="font-bold text-lg text-dark-slate mb-2">No applications yet</h3>
                      <p className="text-slate-gray text-sm">
                        {selectedPostedJob.external_apply_url
                          ? "Applicants are being sent to your external site. Check the Referrals tab to see who clicked through."
                          : "When members apply, they'll appear here."}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 mt-4">
                      {applications.map(app => {
                        const stageColors = { new: 'bg-blue-50 text-blue-700', reviewed: 'bg-indigo-50 text-indigo-700', contacted: 'bg-purple-50 text-purple-700', interviewed: 'bg-amber-50 text-amber-700', offered: 'bg-emerald-50 text-emerald-700', hired: 'bg-green-100 text-green-700', rejected: 'bg-red-50 text-red-700' };
                        return (
                          <div key={app.application_id} className="bg-white rounded-xl p-3 border border-slate-100 hover:shadow-ambient transition-shadow cursor-pointer flex items-center gap-3"
                            onClick={() => setSelectedApplication(app)}>
                            {app.candidate?.profile_photo ? (
                              <img src={app.candidate.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-mint-fresh flex items-center justify-center text-dark-slate font-semibold flex-shrink-0">
                                {app.candidate?.name?.charAt(0) || '?'}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-dark-slate truncate">{app.candidate?.name || 'Applicant'}</p>
                              <p className="text-xs text-slate-gray truncate">
                                {app.candidate?.title || ''}{app.candidate?.institution ? ` · ${app.candidate.institution}` : ''}
                              </p>
                              <p className="text-[10px] text-slate-gray">{app.created_at ? formatDistanceToNow(new Date(app.created_at), { addSuffix: true }) : ''}</p>
                            </div>
                            <Badge variant="secondary" className={`text-[10px] ${stageColors[app.stage] || 'bg-slate-100'}`}>
                              {(app.stage || 'new').charAt(0).toUpperCase() + (app.stage || 'new').slice(1)}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}

                {/* Referrals tab */}
                {applicationsTab === 'referrals' && (
                  <>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-800">
                      <strong>Referrals</strong> are members who clicked the "Apply at {selectedPostedJob.company}" button and were sent to your external application page. Reach out to them directly to check if they completed their application.
                    </div>
                    {loadingReferrals ? (
                      <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-gray" /></div>
                    ) : referrals.length === 0 ? (
                      <div className="text-center py-12">
                        <ExternalLink className="w-12 h-12 text-slate-gray mx-auto mb-4" />
                        <h3 className="font-bold text-lg text-dark-slate mb-2">No referrals yet</h3>
                        <p className="text-slate-gray text-sm">Members who click through to your external application site will appear here.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {referrals.map(ref => (
                          <div key={ref.referral_id} className="bg-white rounded-xl p-3 border border-slate-100 hover:shadow-ambient transition-shadow flex items-center gap-3">
                            {ref.user_profile_photo ? (
                              <img src={ref.user_profile_photo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-mint-fresh flex items-center justify-center text-dark-slate font-semibold flex-shrink-0">
                                {ref.user_name?.charAt(0) || '?'}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-dark-slate truncate">{ref.user_name || 'User'}</p>
                              <p className="text-xs text-slate-gray truncate">
                                {ref.user_title || ''}{ref.user_company ? ` at ${ref.user_company}` : ''}{ref.user_institution ? ` · ${ref.user_institution}` : ''}
                              </p>
                              <p className="text-[10px] text-slate-gray">
                                First clicked {ref.first_clicked ? formatDistanceToNow(new Date(ref.first_clicked), { addSuffix: true }) : ''}
                                {ref.click_count > 1 && ` · ${ref.click_count} clicks`}
                              </p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              {ref.user_email && (
                                <a href={`mailto:${ref.user_email}`} className="p-2 rounded-full hover:bg-slate-100" title={ref.user_email}>
                                  <FileText className="w-4 h-4 text-slate-gray" />
                                </a>
                              )}
                              <button onClick={() => { setShowPostedJobs(false); navigate(`/chat/${ref.user_id}`); }}
                                className="p-2 rounded-full hover:bg-slate-100" title="Message">
                                <MessageCircle className="w-4 h-4 text-slate-gray" />
                              </button>
                              <button onClick={() => { setShowPostedJobs(false); navigate(`/profile/${ref.user_id}`); }}
                                className="p-2 rounded-full hover:bg-slate-100" title="View profile">
                                <UserPlus className="w-4 h-4 text-slate-gray" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-dark-slate flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />My Job Postings
                  </DialogTitle>
                </DialogHeader>
                {loadingPosted ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-gray" /></div>
                ) : postedJobs.length === 0 ? (
                  <div className="text-center py-12">
                    <Briefcase className="w-12 h-12 text-slate-gray mx-auto mb-4" />
                    <h3 className="font-bold text-lg text-dark-slate mb-2">No jobs posted yet</h3>
                    <p className="text-slate-gray text-sm mb-4">Post your first job to start receiving applications.</p>
                    <Button onClick={() => { setShowPostedJobs(false); setShowCreate(true); }} className="bg-dark-slate text-white rounded-full">
                      <Plus className="w-4 h-4 mr-2" />Post a Job
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 mt-4">
                    {postedJobs.map(job => (
                      <div key={job.job_id} className="bg-white rounded-xl p-4 border border-slate-100 hover:shadow-ambient transition-shadow">
                        <div className="flex items-start gap-3">
                          <CompanyLogo company={job.company} logo={job.company_logo} size="md" />
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openJobApplications(job)}>
                            <h3 className="font-semibold text-dark-slate line-clamp-1">{job.title}</h3>
                            <p className="text-sm text-slate-gray">{job.company}</p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <Badge className="bg-dark-slate text-white text-[10px]">
                                {job.application_count || 0} applications
                              </Badge>
                              {job.new_application_count > 0 && (
                                <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                                  {job.new_application_count} new
                                </Badge>
                              )}
                              {job.external_apply_url && (
                                <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                                  {job.referral_count || 0} referred externally
                                </Badge>
                              )}
                              {job.is_active === false && (
                                <Badge className="bg-slate-200 text-slate-600 text-[10px]">Closed</Badge>
                              )}
                            </div>
                            <p className="text-[10px] text-slate-gray mt-1">Posted {job.created_at ? formatDistanceToNow(new Date(job.created_at), { addSuffix: true }) : ''}</p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={(e) => { e.stopPropagation(); openEditJob(job); }}
                              className="p-2 rounded-full hover:bg-slate-100" title="Edit job">
                              <Edit2 className="w-4 h-4 text-slate-gray" />
                            </button>
                            <button onClick={() => openJobApplications(job)} className="p-2 rounded-full hover:bg-slate-100" title="View applications">
                              <ChevronRight className="w-5 h-5 text-slate-gray" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ═══════════ Edit Job Dialog ═══════════ */}
        <Dialog open={!!editJobDialog} onOpenChange={(open) => { if (!open) setEditJobDialog(null); }}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-dark-slate flex items-center gap-2">
                <Edit2 className="w-5 h-5" />Edit Job Posting
              </DialogTitle>
            </DialogHeader>
            {editJobDialog && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Job Title *</Label>
                    <Input value={editJob.title} onChange={(e) => setEditJob({ ...editJob, title: e.target.value })} className="mt-1 rounded-xl" />
                  </div>
                  <div>
                    <Label>Company *</Label>
                    <Input value={editJob.company} onChange={(e) => setEditJob({ ...editJob, company: e.target.value })} className="mt-1 rounded-xl" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Location</Label>
                    <Input value={editJob.location} onChange={(e) => setEditJob({ ...editJob, location: e.target.value })} className="mt-1 rounded-xl" />
                  </div>
                  <div>
                    <Label>Job Type</Label>
                    <Select value={editJob.job_type} onValueChange={(v) => setEditJob({ ...editJob, job_type: v })}>
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {JOB_TYPES.filter(t => t.value !== 'all').map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Work Mode</Label>
                    <Select value={editJob.work_mode || 'none'} onValueChange={(v) => setEditJob({ ...editJob, work_mode: v === 'none' ? '' : v })}>
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not specified</SelectItem>
                        {WORK_MODES.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Experience</Label>
                    <Select value={editJob.experience_level || 'none'} onValueChange={(v) => setEditJob({ ...editJob, experience_level: v === 'none' ? '' : v })}>
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not specified</SelectItem>
                        {EXPERIENCE_LEVELS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={editJob.category || 'none'} onValueChange={(v) => setEditJob({ ...editJob, category: v === 'none' ? '' : v })}>
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="none">Not specified</SelectItem>
                        {allCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Min Salary</Label>
                    <Input type="number" value={editJob.salary_min} onChange={(e) => setEditJob({ ...editJob, salary_min: e.target.value })} className="mt-1 rounded-xl" />
                  </div>
                  <div>
                    <Label>Max Salary</Label>
                    <Input type="number" value={editJob.salary_max} onChange={(e) => setEditJob({ ...editJob, salary_max: e.target.value })} className="mt-1 rounded-xl" />
                  </div>
                </div>
                <div>
                  <Label>Description *</Label>
                  <div className="mt-1">
                    <RichTextEditor
                      value={editJob.description}
                      onChange={(v) => setEditJob({ ...editJob, description: v })}
                      minHeight={140}
                    />
                  </div>
                </div>
                <div>
                  <Label>Requirements (one per line)</Label>
                  <div className="mt-1">
                    <RichTextEditor
                      value={editJob.requirements}
                      onChange={(v) => setEditJob({ ...editJob, requirements: v })}
                      minHeight={100}
                    />
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <Label className="text-sm font-semibold">
                    External Application URL <span className="text-slate-gray font-normal">(optional)</span>
                  </Label>
                  <Input type="url" value={editJob.external_apply_url || ''}
                    onChange={(e) => setEditJob({ ...editJob, external_apply_url: e.target.value })}
                    placeholder="https://company.com/careers/job-id" className="mt-2 rounded-xl" />
                  <p className="text-[11px] text-slate-gray mt-2">If set, the "Apply Now" button sends applicants to this URL. Referrals are tracked in My Postings.</p>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-xl bg-slate-50">
                  <input type="checkbox" id="is_active" checked={editJob.is_active !== false}
                    onChange={(e) => setEditJob({ ...editJob, is_active: e.target.checked })}
                    className="w-4 h-4 rounded" />
                  <label htmlFor="is_active" className="text-sm text-dark-slate cursor-pointer">
                    Job is active and accepting applications
                    <span className="block text-xs text-slate-gray">Uncheck to close the listing without deleting it</span>
                  </label>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={saveEditJob} disabled={savingEditJob}
                    className="flex-1 h-12 bg-dark-slate text-white rounded-full font-semibold">
                    {savingEditJob ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
                  </Button>
                  <Button variant="outline" onClick={() => setEditJobDialog(null)} className="h-12 rounded-full px-6">Cancel</Button>
                  <Button variant="outline" onClick={() => deleteJob(editJobDialog)} disabled={deletingJob}
                    className="h-12 rounded-full px-4 text-red-600 border-red-200 hover:bg-red-50">
                    {deletingJob ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Jobs;
